import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Truck,
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Star,
  Edit,
  Trash2,
  Filter,
  Download,
  Eye,
  DollarSign,
  Calendar,
  X,
  Package
} from 'lucide-react';
import api from '../api/config';
import BusinessPartnerModal from '../components/BusinessPartnerModal';
import ProductsModal from '../components/ProductsModal';
import './SuppliersPage.css';

const SuppliersPage = () => {
  const location = useLocation();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [openContactsDirectly, setOpenContactsDirectly] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedSupplierForProducts, setSelectedSupplierForProducts] = useState(null);
  const [products, setProducts] = useState([]);
  const [statistics, setStatistics] = useState({
    suppliers: 0,
    totalContacts: 0
  });
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Ocultar header cuando modal está abierto en móvil
  useEffect(() => {
    if (showModal) {
      document.body.classList.add('suppliers-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('suppliers-modal-open');
      document.body.style.overflow = '';
    }

    return () => {
      document.body.classList.remove('suppliers-modal-open');
      document.body.style.overflow = '';
    };
  }, [showModal]);

  // Ocultar header cuando modal de eliminar está abierto en móvil
  useEffect(() => {
    if (showDeleteModal || showBulkDeleteModal) {
      document.body.classList.add('suppliers-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('suppliers-modal-open');
      document.body.style.overflow = '';
    }

    return () => {
      document.body.classList.remove('suppliers-modal-open');
      document.body.style.overflow = '';
    };
  }, [showDeleteModal, showBulkDeleteModal]);

  useEffect(() => {
    loadSuppliers();
    loadStatistics();

    // Verificar si se debe abrir el modal desde la URL
    const params = new URLSearchParams(location.search);
    if (params.get('openModal') === 'true') {
      setSelectedSupplier(null);
      setShowModal(true);
      // Limpiar el parámetro de la URL después de abrir el modal
      window.history.replaceState({}, '', '/proveedores');
    }
  }, [location]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);

      // Obtener el companyId de la empresa activa
      const empresaData = localStorage.getItem('empresaActiva');
      let companyId = '3'; // Default: SOBEK INGENIEROS SUPPLY

      if (empresaData) {
        try {
          const parsed = JSON.parse(empresaData);
          companyId = String(parsed.id || parsed.empresaId || '3');
        } catch {
          if (!isNaN(empresaData)) {
            companyId = empresaData;
          }
        }
      }

      const response = await api.get('/business-partners/suppliers', {
        params: {
          search: searchTerm,
          category: selectedCategory,
          companyId: companyId  // Agregar companyId como query parameter
        }
      });
      setSuppliers(response.data.data || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      // Obtener el companyId de la empresa activa
      const empresaData = localStorage.getItem('empresaActiva');
      let companyId = '3'; // Default: SOBEK INGENIEROS SUPPLY

      if (empresaData) {
        try {
          const parsed = JSON.parse(empresaData);
          companyId = String(parsed.id || parsed.empresaId || '3');
        } catch {
          if (!isNaN(empresaData)) {
            companyId = empresaData;
          }
        }
      }

      const response = await api.get('/business-partners/statistics', {
        params: { companyId: companyId }
      });
      const stats = response.data.data;
      setStatistics({
        suppliers: stats.suppliers,
        totalContacts: stats.supplierContacts || 0  // Usar solo contactos de proveedores
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      loadSuppliers();
    }, 500);
  };

  const handleAddSupplier = () => {
    setSelectedSupplier(null);
    setShowModal(true);
  };

  const handleEditSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setOpenContactsDirectly(false);
    setShowModal(true);
  };

  const handleManageContacts = (supplier) => {
    setSelectedSupplier(supplier);
    setOpenContactsDirectly(true);
    setShowModal(true);
  };

  const handleManageProducts = async (supplier) => {
    setSelectedSupplierForProducts(supplier);

    // Cargar productos existentes del proveedor
    try {
      const response = await api.get(`/business-partners/${supplier.id}/products`);

      if (response.data.success) {
        setProducts(response.data.products || []);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }

    setShowProductsModal(true);
  };

  const handleDeleteSupplier = async () => {
    try {
      await api.delete(`/business-partners/${supplierToDelete.id}`);
      setShowDeleteModal(false);
      setSupplierToDelete(null);
      loadSuppliers();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      alert('Error al eliminar el proveedor');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSupplierToDelete(null);
  };

  const handleSelectSupplier = (supplierId) => {
    setSelectedSuppliers(prev => {
      if (prev.includes(supplierId)) {
        return prev.filter(id => id !== supplierId);
      } else {
        return [...prev, supplierId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSuppliers.length === suppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(suppliers.map(s => s.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Eliminar cada proveedor seleccionado
      await Promise.all(
        selectedSuppliers.map(supplierId =>
          api.delete(`/business-partners/${supplierId}`)
        )
      );
      setShowBulkDeleteModal(false);
      setSelectedSuppliers([]);
      loadSuppliers();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting suppliers:', error);
      alert('Error al eliminar los proveedores seleccionados');
    }
  };

  const handleCancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedSupplier(null);
    setOpenContactsDirectly(false);
    loadSuppliers();
    loadStatistics();
  };

  const getRatingStars = (rating) => {
    return Array(5).fill(0).map((_, index) => (
      <Star
        key={index}
        size={14}
        fill={index < rating ? '#FFD700' : 'none'}
        stroke={index < rating ? '#000' : '#000'}
        strokeWidth={2}
      />
    ));
  };

  const getCategoryBadge = (category) => {
    const categoryLabels = {
      'A': 'Estratégico',
      'B': 'Importante',
      'C': 'Regular'
    };
    return categoryLabels[category] ? (
      <span className="category-badge">
        {categoryLabels[category]}
      </span>
    ) : null;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  return (
    <div className="suppliers-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <Truck size={28} />
            Proveedores
          </h1>
          <p className="page-description">
            Gestiona tus proveedores y contactos de compras
          </p>
        </div>

        <div className="header-actions">
          <button className="btn-primary" onClick={handleAddSupplier}>
            <Plus size={18} />
            Nuevo Proveedor
          </button>
        </div>
      </div>

      {selectedSuppliers.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '20px',
          marginTop: '-10px'
        }}>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            style={{
              backgroundColor: 'white',
              color: '#000',
              border: '2px solid #000',
              padding: '10px 20px',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              fontFamily: '"Space Grotesk", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#000';
              e.target.style.color = '#c4fffc';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#000';
            }}
          >
            <Trash2 size={18} />
            Eliminar {selectedSuppliers.length} seleccionado{selectedSuppliers.length > 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="statistics-grid">
        <div className="stat-card">
          <div className="stat-icon suppliers">
            <Truck size={24} />
          </div>
          <div className="stat-info">
            <h3>{statistics.suppliers}</h3>
            <p>Total Proveedores</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon contacts">
            <Users size={24} />
          </div>
          <div className="stat-info">
            <h3>{statistics.totalContacts}</h3>
            <p>Total Contactos</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por nombre, RUT..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        <div className="filter-controls">
        </div>
      </div>

      {/* Suppliers List */}
      <div className="suppliers-grid">
        {loading ? (
          <div className="loading-state">Cargando proveedores...</div>
        ) : suppliers.length === 0 ? (
          <div className="empty-state">
            <Truck size={48} />
            <h3>No hay proveedores registrados</h3>
            <p>Comienza agregando tu primer proveedor</p>
            <button className="btn-primary" onClick={handleAddSupplier}>
              <Plus size={18} />
              Agregar Proveedor
            </button>
          </div>
        ) : (
          suppliers.map(supplier => (
            <div key={supplier.id} className="supplier-card" style={{ position: 'relative' }}>
              <input
                type="checkbox"
                checked={selectedSuppliers.includes(supplier.id)}
                onChange={() => handleSelectSupplier(supplier.id)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  zIndex: 10,
                  accentColor: '#000'
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="supplier-header">
                <div className="supplier-avatar-section">
                  <div className="supplier-avatar">
                    {(supplier.tradeName || supplier.legalName).substring(0, 2).toUpperCase()}
                  </div>
                  <div className="supplier-main-info">
                    <h3>{supplier.tradeName || supplier.legalName}</h3>
                    {supplier.tradeName && (
                      <p className="legal-name">{supplier.legalName}</p>
                    )}
                    <p className="tax-id"><strong>RUT: {supplier.taxId}</strong></p>
                  </div>
                </div>
                <div className="supplier-badges" style={{ marginTop: '30px' }}>
                  {supplier.isClient === 1 && (
                    <span className="dual-badge">También Cliente</span>
                  )}
                </div>
              </div>

              <div className="supplier-info">
                {supplier.industry && (
                  <div className="info-item">
                    <span className="info-label">Industria:</span>
                    <span>{supplier.industry}</span>
                  </div>
                )}

                {supplier.paymentTerms && (
                  <div className="info-item">
                    <Calendar size={14} />
                    <span>Términos de pago: {supplier.paymentTerms} días</span>
                  </div>
                )}

                {supplier.creditLimit && (
                  <div className="info-item">
                    <DollarSign size={14} />
                    <span>Límite de crédito: {formatCurrency(supplier.creditLimit)}</span>
                  </div>
                )}

                {supplier.address && (
                  <div className="info-item">
                    <MapPin size={14} />
                    <span>{supplier.address}, {supplier.city}</span>
                  </div>
                )}

                {supplier.phone && (
                  <div className="info-item">
                    <Phone size={14} />
                    <span>{supplier.phone}</span>
                  </div>
                )}

                {supplier.email && (
                  <div className="info-item">
                    <Mail size={14} />
                    <span>{supplier.email}</span>
                  </div>
                )}

                <div className="rating-section">
                  <span>Calificación:</span>
                  <div className="rating-stars">
                    {getRatingStars(supplier.rating)}
                  </div>
                </div>
              </div>

              <div className="supplier-actions">
                <button
                  className="btn-action products"
                  onClick={() => handleManageProducts(supplier)}
                  title="Gestionar Productos/Servicios"
                >
                  <Package size={16} />
                  <span className="products-count">{supplier.products?.length || 0}</span>
                </button>
                <button
                  className="btn-action contacts"
                  onClick={() => handleManageContacts(supplier)}
                  title="Gestionar Contactos"
                >
                  <Mail size={16} />
                  <span className="contacts-count">{supplier.contacts.length}</span>
                </button>
                <button
                  className="btn-action edit"
                  onClick={() => handleEditSupplier(supplier)}
                  title="Editar"
                >
                  <Edit size={16} />
                </button>
                <button
                  className="btn-action delete"
                  onClick={() => {
                    setSupplierToDelete(supplier);
                    setShowDeleteModal(true);
                  }}
                  title="Eliminar"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {supplier.totalPurchases > 0 && (
                <div className="supplier-stats">
                  <div className="stat">
                    <span className="stat-label">Compras Totales:</span>
                    <span className="stat-value">
                      {formatCurrency(supplier.totalPurchases)}
                    </span>
                  </div>
                  {supplier.lastTransactionDate && (
                    <div className="stat">
                      <span className="stat-label">Última transacción:</span>
                      <span className="stat-date">
                        {new Date(supplier.lastTransactionDate).toLocaleDateString('es-CL')}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal for Add/Edit Supplier */}
      {showModal && (
        <BusinessPartnerModal
          isOpen={showModal}
          onClose={handleModalClose}
          partner={selectedSupplier}
          type="supplier"
          openContactsDirectly={openContactsDirectly}
        />
      )}

      {/* Products Modal */}
      {showProductsModal && selectedSupplierForProducts && (
        <ProductsModal
          isOpen={showProductsModal}
          onClose={async (updatedProducts) => {
            setShowProductsModal(false);

            // Save products to backend
            if (updatedProducts && updatedProducts.length >= 0) {
              try {
                const response = await api.post(`/business-partners/${selectedSupplierForProducts.id}/products`, {
                  products: updatedProducts
                });

                if (response.data.success) {
                  console.log('Products saved successfully');
                  // Recargar proveedores para actualizar contadores
                  loadSuppliers();
                  loadStatistics();
                } else {
                  console.error('Error saving products:', response.data.message);
                }
              } catch (error) {
                console.error('Error saving products:', error);
              }
            }

            setSelectedSupplierForProducts(null);
          }}
          supplier={selectedSupplierForProducts}
          products={products}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && supplierToDelete && (
        <div className="modal-overlay delete-modal-custom">
          <div className="modal">
            <div className="modal-header">
              <h2>Confirmar Eliminación</h2>
              <button
                className="modal-close"
                onClick={handleCancelDelete}
                type="button"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="delete-confirmation-content">
                <div className="delete-icon">
                  <Trash2 size={48} />
                </div>
                <h3>¿Está seguro de eliminar este proveedor?</h3>
                <p>
                  <strong>{supplierToDelete.tradeName || supplierToDelete.legalName}</strong>
                </p>
                <p className="warning-text">
                  Esta acción no se puede deshacer. Se eliminará el proveedor y todos sus contactos asociados.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancelDelete}
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary-delete"
                onClick={handleDeleteSupplier}
              >
                <Trash2 size={16} />
                Eliminar Proveedor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="modal-overlay delete-modal-custom">
          <div className="modal">
            <div className="modal-header">
              <h2>Confirmar Eliminación Masiva</h2>
              <button
                className="modal-close"
                onClick={handleCancelBulkDelete}
                type="button"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="delete-confirmation-content">
                <div className="delete-icon">
                  <Trash2 size={48} />
                </div>
                <h3>¿Está seguro de eliminar {selectedSuppliers.length} proveedor{selectedSuppliers.length > 1 ? 'es' : ''}?</h3>
                <p className="warning-text">
                  Esta acción no se puede deshacer. Se eliminarán todos los proveedores seleccionados, sus contactos asociados y productos.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancelBulkDelete}
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary-delete"
                onClick={handleBulkDelete}
              >
                <Trash2 size={16} />
                Eliminar {selectedSuppliers.length} Proveedor{selectedSuppliers.length > 1 ? 'es' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SuppliersPage;